import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import SectionHeading from "../../components/SectionHeading";
import { processContent } from "./homeContent";
import "./HomeProcess.css";

/* ============================================================================
   "Our Certification Process"

   Two rendering paths over the SAME content:

   1. CONVEYOR (>= 1280px, motion allowed) — the six steps ride a scroll-driven
      conveyor through three fixed slots on a descending stair. All of the
      geometry is CSS (HomeProcess.css); this file writes exactly one number,
      `--mp-p` (0 -> 3, fractional), onto the stage.

   2. GRID (everything narrower, reduced motion, or no JS) — the responsive
      1 / 2 / 3-column grid this section has always used, with its framer-motion
      stagger intact. The conveyor needs ~1176px of horizontal room for its
      three slots, which a tablet does not have, and the stair would simply be
      clipped; below the breakpoint the cards stack cleanly instead.

   WHY THE TWO PATHS ARE A RENDER SWITCH AND NOT ONE MARKUP WITH TWO
   STYLESHEETS: the grid path's entrance is framer-motion, which writes
   `opacity` and `transform` into the inline style of each card. The conveyor
   owns both (`opacity: var(--mp-o)` on the card, `translate` on the slot
   wrapper) and the two would clobber each other. Worse, cards 4-6 rest
   off-stage where the section clips them, so IntersectionObserver — which
   accounts for ancestor clipping — would never fire for them and they would
   stay pinned at the hidden variant's `opacity: 0` forever. The conveyor IS
   their entrance animation; they need no reveal.
   ========================================================================= */

const stepVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

/** visible slots on the stage; 6 cards through 3 slots = 3 advances */
const SLOTS = 3;

const CONVEYOR_QUERY =
  "(min-width: 1280px) and (prefers-reduced-motion: no-preference)";

const useConveyorEnabled = () => {
  const [enabled, setEnabled] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(CONVEYOR_QUERY).matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mq = window.matchMedia(CONVEYOR_QUERY);
    const onChange = (event) => setEnabled(event.matches);
    setEnabled(mq.matches);
    // Safari < 14 only has the deprecated listener API.
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return enabled;
};

/** the card interior, identical on both paths */
const StepCard = ({ step }) => (
  <>
    {/* title left, numeral right — the numeral IS the step marker, so there
        is no "step N of six" caption on the card */}
    <div className="home-process__head">
      <h4 className="home-process__title">{step.title}</h4>
      <span className="home-process__num" aria-hidden="true">
        {String(step.number).padStart(2, "0")}
      </span>
    </div>
    <p className="home-process__desc">{step.description}</p>
    {/* decorative: the step is already named in the heading beside it, so the
        illustration adds nothing for a screen reader */}
    <span className="home-process__art" aria-hidden="true">
      <img className="home-process__art-img" src={step.image} alt="" loading="lazy" />
    </span>
  </>
);

const ProcessConveyor = () => {
  const trackRef = useRef(null);
  const stageRef = useRef(null);

  const write = useCallback(() => {
    const track = trackRef.current;
    const stage = stageRef.current;
    if (!track || !stage) return;
    /* Self-calibrating: while the stage is pinned its rect stays put and the
       track's slides up, so (stage.top - track.top) IS the distance travelled
       inside the track — 0 before the pin, the full runway once it releases.
       No reading of the sticky `top`, and it holds at every rem scale and
       viewport height without a single hardcoded number. */
    const advances = processContent.steps.length - SLOTS;
    const t = track.getBoundingClientRect();
    const s = stage.getBoundingClientRect();
    const per = (t.height - s.height) / advances;
    let p = per > 0 ? (s.top - t.top) / per : 0;
    if (p < 0) p = 0;
    else if (p > advances) p = advances;
    stage.style.setProperty("--mp-p", p.toFixed(4));
  }, []);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      // rAF coalesces the writes down to one per frame
      if (!raf) {
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          write();
        });
      }
    };

    write();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [write]);

  return (
    <div className="home-process__track" ref={trackRef}>
      <ol className="home-process__stage" ref={stageRef}>
        {processContent.steps.map((step, i) => (
          <li
            key={step.number}
            className="home-process__slot"
            data-step={i + 1}
            style={{ "--mp-i": i }}
          >
            <article className="brand-card home-process__card">
              <StepCard step={step} />
            </article>
          </li>
        ))}
        {/* the faint panels under slots A and B — they belong to the slots, not
            to any card, so they are static siblings. Last in the DOM only so
            `data-step` stays 1..6; z-index puts them behind every card. */}
        <span className="home-process__fill home-process__fill--a" aria-hidden="true" />
        <span className="home-process__fill home-process__fill--b" aria-hidden="true" />
      </ol>
    </div>
  );
};

const ProcessGrid = () => (
  <motion.ol
    className="home-process__grid"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.15 }}
    variants={{ visible: { transition: { staggerChildren: 0.09 } } }}
  >
    {processContent.steps.map((step) => (
      <motion.li
        key={step.number}
        variants={stepVariants}
        className="brand-card home-process__card p-6 lg:p-8"
      >
        <StepCard step={step} />
      </motion.li>
    ))}
  </motion.ol>
);

const HomeProcess = () => {
  const conveyor = useConveyorEnabled();

  return (
    <section
      className="home-process bg-gray-50 py-14 lg:py-20"
      aria-labelledby="home-process-heading"
    >
      <span className="home-process__wash" aria-hidden="true" />
      <div className="sectionlayout">
        <SectionHeading
          id="home-process-heading"
          title={processContent.title}
          subtitle={processContent.subtitle}
          rule={false}
        />
        {conveyor ? <ProcessConveyor /> : <ProcessGrid />}
      </div>
    </section>
  );
};

export default HomeProcess;
